<template>
  <div
    v-if="$store.getters.authenticated"
    class="d-flex align-center app-bar-user"
  >
    <v-btn
      v-if="hasPlan || maxFreeAnalyses > 0"
      :outlined="barChipOutlined"
      :color="barChipColor"
      class="usage-chip-btn text-none"
      depressed
      :aria-label="String($t('subscriptions.barTooltip'))"
      @click="openAccount"
    >
      <v-tooltip bottom max-width="400">
        <template #activator="{ on }">
          <span
            v-on="on"
            class="usage-chip-inner"
          >
            <v-icon class="usage-chip-person">person</v-icon>
            <span class="usage-chip-content">
              <span class="usage-chip-label">
                {{ usageChip.kind === 'free' ? $t('subscriptions.barLabelFree') : $t('subscriptions.barLabel') }}
              </span>
              <span class="usage-chip-value">
                <template v-if="usageChip.kind === 'loading'">
                  <span aria-busy="true">{{ $t('subscriptions.usageLoading') }}</span>
                </template>

                <template v-else-if="usageChip.kind === 'free'">
                  <v-icon small class="mr-1">analytics</v-icon>
                  <v-fade-transition mode="out-in">
                    <span :key="freeAnalysesLeft">{{ freeAnalysesLeft }}</span>
                  </v-fade-transition>
                  / {{ maxFreeAnalyses }}
                  <span v-if="extraInspirations > 0" class="ml-2">
                    <v-icon small class="mr-1">add_circle</v-icon>
                    <v-fade-transition mode="out-in">
                      <span :key="extraInspirations">{{ extraInspirations }}</span>
                    </v-fade-transition>
                  </span>
                </template>

                <template v-else-if="usageChip.kind === 'capped'">
                  <v-icon small class="mr-1">article</v-icon>
                  <v-fade-transition mode="out-in">
                    <span :key="usageChip.used">{{ usageChip.used }}</span>
                  </v-fade-transition>
                  / {{ usageChip.max }}
                  <span v-if="extraInspirations > 0" class="ml-2">
                    <v-icon small class="mr-1">add_circle</v-icon>
                    <v-fade-transition mode="out-in">
                      <span :key="extraInspirations">{{ extraInspirations }}</span>
                    </v-fade-transition>
                  </span>
                </template>

                <template v-else>
                  <v-icon small class="mr-1">article</v-icon>
                  <v-fade-transition mode="out-in">
                    <span :key="articlesCreatedThisPeriod">{{ articlesCreatedThisPeriod }}</span>
                  </v-fade-transition>
                  / ∞
                  <span v-if="extraInspirations > 0" class="ml-2">
                    <v-icon small class="mr-1">add_circle</v-icon>
                    <v-fade-transition mode="out-in">
                      <span :key="extraInspirations">{{ extraInspirations }}</span>
                    </v-fade-transition>
                  </span>
                </template>
              </span>
            </span>
          </span>
        </template>
        
        <div class="text-center">
          <div class="font-weight-bold">{{ $t('subscriptions.barTooltip') }}</div>
          <template v-if="usageChip.kind === 'loading'">
            <div class="mt-2">{{ $t('subscriptions.usageLoadingTooltip') }}</div>
          </template>
          <template v-else-if="usageChip.kind === 'free'">
            <div class="mt-2">{{ $t('subscriptions.usage.freeAnalyses', [freeAnalysesLeft, maxFreeAnalyses]) }}</div>
            <div class="mt-2 text-left">{{ freeAnalysesExplain }}</div>
            <div class="mt-1">{{ $t('subscriptions.usage.freeAnalysesReset') }}</div>
            <div v-if="extraInspirations > 0" class="mt-2">
              {{ $t('subscriptions.usage.extraInspirationsExplain', [extraInspirations]) }}
            </div>
          </template>
          <template v-else-if="usageChip.kind === 'capped'">
            <div class="mt-2">{{ $t('subscriptions.usageTooltip', [usageChip.used, usageChip.max]) }}</div>
            <div v-if="extraInspirations > 0" class="mt-2">
              {{ $t('subscriptions.usage.extraInspirationsAfterPlan', [extraInspirations]) }}
            </div>
          </template>
          <template v-else>
            <div class="mt-2">{{ $t('subscriptions.usage.unlimitedArticles', [articlesCreatedThisPeriod]) }}</div>
            <div v-if="extraInspirations > 0" class="mt-2">
              {{ $t('subscriptions.usage.extraInspirationsAfterPlan', [extraInspirations]) }}
            </div>
          </template>
        </div>
      </v-tooltip>
    </v-btn>
  </div>
</template>

<script lang="ts">
    import Vue from "vue";
    import UserPlanMixin from "@/mixins/UserPlanMixin";

    export default Vue.extend({
        name: "AppBarUserInfo",
        mixins: [UserPlanMixin],
        methods: {
            openAccount: function () {
                if (this.$route.path !== '/account') {
                    this.$router.push('/account');
                }
            }
        },
        computed: {
            freeAnalysesExplain(): string {
                if (this.freeAnalysesLeft <= 0) {
                    return String(this.$t('subscriptions.usage.freeAnalysesNone'));
                }
                return String(this.$t('subscriptions.usage.freeAnalysesExplain', {
                    max: this.maxFreeAnalyses,
                    used: Math.max(this.analyzeArticlesThisMonth, this.freeAnalysesUsed),
                    left: this.freeAnalysesLeft,
                }));
            },
            barChipOutlined(): boolean {
                if (!this.planLoaded) {
                    return true;
                }
                if (!this.hasPlan && this.freeAnalysesLeft <= 0) {
                    return false;
                }
                return !this.hasLimitedPlan;
            },
            barChipColor(): string {
                if (!this.planLoaded) {
                    return 'white';
                }
                if (!this.hasPlan && this.freeAnalysesLeft <= 0) {
                    return 'error';
                }
                if (this.hasLimitedPlan) {
                    return this.usageColor;
                }
                // Outlined chip sits on the blue app bar; warning/accent oranges and greens
                // do not contrast enough. White border and text stay readable.
                return 'white';
            }
        }
    });
</script>

<style scoped>
.app-bar-user {
  flex-shrink: 0;
}
.usage-chip-btn {
  height: 44px !important;
  min-height: 44px;
  padding: 4px 10px !important;
  margin: 0 !important;
  flex-shrink: 0;
}
.usage-chip-inner {
  display: flex;
  flex-direction: row;
  align-items: center;
  min-width: 0;
}
.usage-chip-person {
  flex-shrink: 0;
  margin-right: 6px;
}
.usage-chip-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.15;
  text-align: left;
  min-width: 0;
}
.usage-chip-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  opacity: 0.9;
  text-transform: none;
  white-space: nowrap;
}
.usage-chip-value {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}
</style>
